<template>
  <div v-if="course" class="term-row justify-content-between">
    <div class="term-col term-col-22">
      <div class="term">
        <div class="term-item term-item-md">{{ $t('src/components/coursesettingsblock.9095') }}</div>
        <div class="term-value">
          <span v-if="optWatchHr">{{ hrValue }}</span>
          <span v-else class="icon icon_small icon-off"></span>
        </div>
      </div>
      <div class="term">
        <div class="term-item term-item-md">{{ $t('src/components/coursesettingsblock.sT') }}:</div>
        <div class="term-value">
          <span class="icon icon_small" :class="optCalcST ? 'icon-on' : 'icon-off'"></span>
        </div>
      </div>
    </div>
    <div class="term-col term-col-22">
      <div class="term">
        <div class="term-item term-item-xl">{{ $t('general.duration') }}:</div>
        <div class="term-value">
          <duration-with-unit
            v-if="optShowDuration"
            :durationValue="course.training_duration"
            :durationUnit="course.training_duration_unit"
            :isReadonly="true"
          ></duration-with-unit>
          <span v-else class="icon icon_small icon-off"></span>
        </div>
      </div>
      <div class="term">
        <div class="term-item term-item-xl">{{ $t('src/components/coursesettingsblock.3807') }}</div>
        <div class="term-value">
          <span class="icon icon_small" :class="optBorgEnd ? 'icon-on' : 'icon-off'"></span>
        </div>
      </div>
    </div>
    <div class="term-col term-col-22">
      <div class="term">
        <div class="term-item term-item-xl">{{ $t('src/components/coursesettingsblock.6547') }}</div>
        <div class="term-value">
          <span class="icon icon_small" :class="optArrhythmia ? 'icon-on' : 'icon-off'"></span>
        </div>
      </div>
      <div class="term">
        <div class="term-item term-item-xl">
          {{ $t('src/components/coursesettingsblock.sessionCompletion') }}:
        </div>
        <div class="term-value">
          <span class="icon icon_small" :class="optAutoSessionStop ? 'icon-on' : 'icon-off'"></span>
        </div>
      </div>
    </div>
    <div class="term-col term-col-22">
      <div class="term">
        <div class="term-item term-item-xl">{{ $t('src/components/coursesettingsblock.informing') }}:</div>
        <div class="term-value">
          <span class="icon icon_small" :class="optShowEvents ? 'icon-on' : 'icon-off'"></span>
        </div>
      </div>
    </div>
    <div
      v-if="!isClosedCourse && isManageCourse"
      class="term-col term-col-10 justify-content-end align-self-center"
    >
      <button class="btn btn-outline-primary" @click="showModal">
        {{ $t('src/components/coursesettingsblock.153') }}
      </button>
    </div>
    <base-modal
      v-if="!isClosedCourse"
      :is-show="isShowModal"
      size="sm"
      mode="apply"
      :is-disabled-btn="isInvalid"
      @close="closeModal"
      @confirm="confirmChanges"
    >
      <template #header>{{ $t('src/components/coursesettingsblock.9828') }}</template>
      <template #body>
        <course-settings-form
          v-if="courseSettings"
          :settings="courseSettings"
          :patient="course.patient"
          @change:settings="updateCourseSettings"
          @set:invalid="setInvalid"
          class="form-in-modal text-left"
        ></course-settings-form>
      </template>
      <template #extra>
        <div class="extra">
          <base-checkbox name="updateSessionSettings" v-model="updateSessionSettings">{{
            $t('src/components/coursesettingsblock.5420')
          }}</base-checkbox>
        </div>
      </template>
    </base-modal>

    <base-modal
      :is-show="isShowModalUpdateSessions"
      @close="closeModalUpdateSessions"
      @cancel="updateSettings(courseSettings, false)"
      @confirm="updateSettings(courseSettings, true)"
    >
      <template #header>{{ $t('src/components/coursesettingsblock.549') }}</template>
      <template #body>{{ $t('src/components/coursesettingsblock.2556') }}</template>
    </base-modal>
  </div>
</template>

<script>
import { status } from '@/main.config'
import { userRights } from '@/mixins/user-rights'
import { systemUnavailable } from '@/mixins/system-unavailable.js'

import('@/styles/components/terms.less')

export default {
  name: 'CourseSettingsBlock',
  components: {
    BaseModal: () => import('@/base/BaseModal.vue'),
    CourseSettingsForm: () => import('@/components/CourseSettingsForm.vue'),
    DurationWithUnit: () => import('@/components/DurationWithUnit.vue'),
    BaseCheckbox: () => import('@/base/BaseCheckbox.vue')
  },
  mixins: [userRights, systemUnavailable],
  props: {
    course: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    isInvalid: false,
    courseSettings: null,
    isShowModal: false,
    isShowModalUpdateSessions: false,
    updateSessionSettings: false
  }),
  computed: {
    isClosedCourse() {
      if (!this.course) {
        return true
      }
      return this.course.status === status.closed || this.course.status === status.closedWrong
    },
    optBorgEnd() {
      return +this.course.opt_borg
    },
    optWatchHr() {
      return +this.course.opt_watch_hr
    },
    optArrhythmia() {
      return +this.course.opt_calc_arrythmias
    },
    optCalcST() {
      return +this.course.opt_calc_st
    },
    optShowEvents() {
      return +this.course.opt_show_events
    },
    optShowDuration() {
      return +this.course.opt_show_duration
    },
    optAutoSessionStop() {
      return +this.course.auto_session_stop
    },
    hrValue() {
      if (!this.optWatchHr) {
        return null
      }
      return +this.course.course_hr_min + '-' + +this.course.course_hr_max
    }
  },
  created() {
    const boolSettings = new Set([
      'opt_borg',
      'opt_borg_start',
      'opt_watch_hr',
      'opt_calc_arrythmias',
      'opt_calc_st',
      'opt_show_events',
      'opt_show_duration',
      'auto_session_stop'
    ])
    const valueSettings = new Set([
      'course_hr_min',
      'course_hr_max',
      'training_duration',
      'training_duration_unit'
    ])
    this.courseSettings = {}
    // eslint-disable-next-line no-unused-vars
    for (let key in this.course) {
      if (boolSettings.has(key)) {
        this.courseSettings[key] = Boolean(this.course[key])
      } else if (valueSettings.has(key)) {
        if (this.course[key] !== null) {
          this.courseSettings[key] = this.course[key]
        }
      }
    }
  },

  methods: {
    saveSettings(courseSettings, updateSessionSettings) {
      if (!courseSettings.opt_show_duration) {
        courseSettings.auto_session_stop = false
      }
      this.$emit('save:settings', courseSettings, updateSessionSettings)
    },
    showModal() {
      if (Number(localStorage.getItem('activeHospital'))) {
        this.isShowModal = true
        this.updateSessionSettings = false
      } else {
        this.$emit('system:unavailable')
      }
    },
    closeModal() {
      this.isShowModal = false
    },
    confirmChanges() {
      if (this.isInvalid) {
        return
      }
      if (this.updateSessionSettings) {
        this.closeModal()
        this.saveSettings(this.courseSettings, this.updateSessionSettings)
      } else {
        this.isShowModalUpdateSessions = true
      }
    },
    updateCourseSettings(settings) {
      this.courseSettings = settings
    },
    setInvalid(isInvalid) {
      this.isInvalid = isInvalid
    },
    updateSettings(courseSettings, updateSessionSettings) {
      this.saveSettings(courseSettings, updateSessionSettings)
      this.closeModalUpdateSessions()
      this.closeModal()
    },
    closeModalUpdateSessions() {
      this.isShowModalUpdateSessions = false
    }
  }
}
</script>
